import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  FC,
} from 'react';
import { Experiment } from '@amplitude/experiment-js-client';
import { ExperimentFlags, ExperimentsContextType } from './types';

const ExperimentsContext = createContext<ExperimentsContextType | undefined>(
  undefined
);

type Props = {
  children: ReactNode;
  apiKey: string;
  userId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userProperties: any;
};

export const ExperimentsProvider: FC<Props> = ({
  children,
  apiKey,
  userId,
  userProperties = {},
}) => {
  const [flags, setFlags] = useState<ExperimentFlags>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeExperiments = async () => {
      try {
        setLoading(true);
        setError(null);

        const experimentClient = Experiment.initialize(apiKey, {
          fallbackVariant: {},
          initialVariants: {},
        });

        const user = {
          user_id: userId || '',
          user_properties: userProperties,
        };

        await experimentClient.fetch(user);

        const variants = experimentClient.all();
        const flagsData: ExperimentFlags = {};

        Object.keys(variants).forEach((key) => {
          const variant = variants[key];
          if (variant && variant.value !== undefined) {
            flagsData[key] = {
              value: variant.value,
              payload: variant.payload,
            };
          }
        });

        setFlags(flagsData);
      } catch (err) {
        console.error('Failed to initialize Amplitude Experiments:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    if (apiKey) {
      initializeExperiments();
    }
  }, [apiKey, userId, userProperties]);

  const getFlag = <T,>(flagKey: string, defaultValue: T): T => {
    const flag = flags[flagKey];
    if (flag && flag.value !== undefined) {
      return flag.value as T;
    }
    return defaultValue;
  };

  const contextValue: ExperimentsContextType = {
    flags,
    loading,
    error,
    getFlag,
  };

  return (
    <ExperimentsContext.Provider value={contextValue}>
      {children}
    </ExperimentsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useExperiments = (): ExperimentsContextType => {
  const context = useContext(ExperimentsContext);
  if (!context) {
    throw new Error(
      'useExperiments must be used within an ExperimentsProvider'
    );
  }
  return context;
};
